import { pipe } from "fp-ts/lib/function"
import React from "react"
import { Icon } from "../../../components"
import useCarrousel from "../../../hooks/useCarrousel"
import { agentName, numberAbreviation } from "../../../lib/format"
import * as M from "../../Model/overviewModel"
import * as O from "fp-ts/Option"

export default function ({
  offers,
  onSelectOffer,
}: {
  offers: M.AgentOffer[]
  onSelectOffer: (offer: M.AgentOffer, id: string) => void
}): JSX.Element {
  const container = React.useRef<HTMLDivElement>(null)
  const prevButton = React.useRef<HTMLButtonElement>(null)
  const nextButton = React.useRef<HTMLButtonElement>(null)

  const { scrollContainerBy } = useCarrousel({
    containerRef: container,
    prevButtonRef: prevButton,
    nextButtonRef: nextButton,
  })

  return (
    <div data-cy="dashboard-offers">
      <div
        className="flex gap-2 mb-[12px]"
        data-cy={`dashboard-offers-carousel`}
      >
        <h4
          className="heading flex-1"
          data-cy={`dashboard-offers-carousel-title`}
        >
          {offers.length === 1
            ? `${offers.length} Offer`
            : `${offers.length} Offers`}
        </h4>
        {offers.length > 1 && (
          <>
            <button
              ref={prevButton}
              data-cy={`dashboard-offers-carousel-left`}
              className="button-icon hidden md:block rotate-180 disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000]"
              onClick={() => scrollContainerBy("left")}
              aria-label="scroll carousel left"
            >
              <Icon.ChevronRight />
            </button>
            <button
              ref={nextButton}
              data-cy={`dashboard-offers-carousel-right`}
              className=" button-icon hidden md:block disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000]"
              onClick={() => scrollContainerBy("right")}
              aria-label="scroll carousel right"
            >
              <Icon.ChevronRight />
            </button>
          </>
        )}
      </div>
      <div ref={container} className="carousel">
        {offers.map(({ agent, offer }, idx) => {
          const { name, value } = numberAbreviation(offer.amount)

          return (
            <button
              id={`offer-${idx}-btn`}
              key={`offers-${idx}`}
              onClick={() =>
                onSelectOffer({ agent, offer }, `offer-${idx}-btn`)
              }
            >
              <div
                className={
                  "card p-[25px] w-[210px] h-[177px] flex-[0_0_auto] bg-slate focus-visible:outline-brick text-white text-left flex flex-col"
                }
                data-cy={`dashboard-offer-card-${idx}`}
              >
                {pipe(
                  offer.priceIncreased,
                  O.match(
                    () => <></>,
                    hasIncreased =>
                      hasIncreased ? (
                        <div
                          className="absolute right-6 top-9 w-[30px] "
                          data-cy={`dashboard-offer-card-${idx}-price-stats`}
                        >
                          <Icon.Stats />
                        </div>
                      ) : (
                        <></>
                      )
                  )
                )}
                <h5 className="heading text-white text-[3.2rem] leading-[0.9] leading tracking-[0.03em]">
                  <span
                    className="text-[0.9em]"
                    data-cy={`dashboard-offer-card-${idx}-amount`}
                  >
                    {value}
                  </span>
                  <span className=" text-[0.8em]">
                    {O.getOrElse(() => "")(name)}
                  </span>
                </h5>
                <p className=" mt-[18px] text-sm text-cutoff">
                  {agentName({
                    brand: agent.brand,
                    branch: agent.branch,
                  })}
                </p>
                <OfferLabel offer={offer} idx={idx} />
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

const OfferLabel = ({
  offer,
  idx,
}: Pick<M.AgentOffer, "offer"> & { idx: number }): JSX.Element => {
  const offerLabel = M.offerStatusLabel(offer.status)

  switch (offer.status) {
    case "Accepted":
    case "Under Offer":
    case "Offer Exchanged":
    case "Completed": {
      return (
        <p
          className="mt-[18px] tag px-2 green"
          data-cy={`dashboard-offer-card-${idx}-${offer.status.toLowerCase()}`}
        >
          {offerLabel}
        </p>
      )
    }
    case "Rejected":
    case "Fell Through": {
      return (
        <p
          className="mt-[18px] tag px-2 red"
          data-cy={`dashboard-offer-card-${idx}-${offer.status.toLowerCase()}`}
        >
          {offerLabel}
        </p>
      )
    }

    default: {
      return (
        <p
          className="mt-[18px] tag px-2 bg-glass-light text-slate"
          data-cy={`dashboard-offer-card-${idx}-review`}
          key={idx}
        >
          {offerLabel}
        </p>
      )
    }
  }
}
